import React, { useState, useEffect } from 'react';

const PricingSection = ({ onOpenPaymentPage, pricing = { price1Friend: 2975, price2Friends: 2975, originalPrice: 7000, enable2Friends: true } }) => {
  const price1 = pricing.price1Friend || 2975;
  const price2 = pricing.price2Friends || 2975;
  const originalPrice1 = 3999;
  const originalPrice2 = Math.round(price2 * 3.33);
  const discount1 = 50;
  const discount2 = Math.round(((originalPrice2 - price2) / originalPrice2) * 100);
  const savings1 = 2000;
  const savings2 = originalPrice2 - price2;
  const formatPrice = (num) => '₹' + Number(num).toLocaleString('en-IN');

  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 5,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const totalSeconds = prev.hours * 3600 + prev.minutes * 60 + prev.seconds - 1;
        
        if (totalSeconds <= 0) {
          return { hours: 0, minutes: 5, seconds: 0 };
        }
        
        return {
          hours: Math.floor(totalSeconds / 3600),
          minutes: Math.floor((totalSeconds % 3600) / 60),
          seconds: totalSeconds % 60
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => num.toString().padStart(2, '0');

  return (
    <section className="relative py-24 bg-gradient-to-b from-gray-900 via-black to-gray-900 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-900/30 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-orange-900/20 via-transparent to-transparent"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-amber-500/30 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          ></div>
        ))}
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Urgency Banner */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-red-500/20 border border-red-500/50 rounded-full text-red-400 animate-bounce-swing">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-bold">Limited Time Offer - Act Now!</span>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
            Yes – I Want This Course
          </h2>
          <p className="text-gray-400">
            Master the Advance Payment Collection System Today
          </p>
        </div>

        {/* Single Card */}
        <div className="max-w-2xl mx-auto">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/40 via-orange-500/40 to-amber-500/40 rounded-3xl blur-xl opacity-60"></div>
            <div className="relative bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl border border-amber-500/30 p-6 text-center overflow-hidden shadow-2xl">
              <div>
                <div className="h-6 mb-2"></div>
                
                {/* Timer */}
                <div className="mb-4">
                  <p className="text-gray-400 text-xs mb-3 uppercase tracking-widest font-medium">Offer Expires In</p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="relative">
                      <div className="absolute inset-0 bg-red-500 rounded-xl blur-sm opacity-40"></div>
                      <div className="relative bg-gradient-to-br from-red-500 to-red-700 text-white w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-black shadow-lg border border-red-400/30">
                        {formatTime(timeLeft.minutes)}
                      </div>
                      <p className="text-gray-500 text-[9px] mt-1 uppercase tracking-wider">Minutes</p>
                    </div>
                    <span className="text-2xl font-black text-amber-500 animate-pulse mb-5">:</span>
                    <div className="relative">
                      <div className="absolute inset-0 bg-red-500 rounded-xl blur-sm opacity-40"></div>
                      <div className="relative bg-gradient-to-br from-red-500 to-red-700 text-white w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-black shadow-lg border border-red-400/30">
                        {formatTime(timeLeft.seconds)}
                      </div>
                      <p className="text-gray-500 text-[9px] mt-1 uppercase tracking-wider">Seconds</p>
                    </div>
                  </div>
                </div>

                <p className="text-red-400 text-xl md:text-2xl font-bold mb-4">
                  Price rises to {formatPrice(originalPrice1)} after timer ends!
                </p>

                <div className="mb-4">
                  <p className="text-red-500 line-through text-xl font-black">{formatPrice(originalPrice1)}</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                      {formatPrice(price1)}
                    </span>
                    <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded-full border border-green-500/30">
                      {discount1}% OFF
                    </span>
                  </div>
                  <p className="text-green-400 font-semibold mt-2 flex items-center justify-center gap-1 text-sm">
                    <span>🎉</span> Save {formatPrice(savings1)} Today!
                  </p>
                </div>
              </div>

              <button 
                onClick={() => onOpenPaymentPage(price1)}
                className="group/btn relative inline-flex items-center justify-center w-full py-4 px-6 text-base font-bold text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-500 rounded-xl shadow-lg shadow-amber-500/30 overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/50"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></span>
                <span className="relative flex items-center gap-2">
                  Yes, I Want This
                  <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-6 text-gray-500">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="text-sm">Secure Payment</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <span className="text-sm">All Cards Accepted</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">Instant Access</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes bounce-swing {
          0%, 100% { 
            transform: translateY(0) rotate(0deg) scale(1);
          }
          10% { 
            transform: translateY(-5px) rotate(-3deg) scale(1.05);
          }
          20% { 
            transform: translateY(-10px) rotate(3deg) scale(1.05);
          }
          30% { 
            transform: translateY(-5px) rotate(-2deg) scale(1.02);
          }
          40% { 
            transform: translateY(0) rotate(2deg) scale(1);
          }
          50% { 
            transform: translateY(0) rotate(0deg) scale(1);
          }
        }
        .animate-bounce-swing {
          animation: bounce-swing 2s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default PricingSection;
